import React, { useCallback, useEffect, useState } from 'react';
import { Championship, ChampionshipEvent, ChampionshipRound, SimgridSignout, Track, Car, CarConfig } from '../types';
import { Plus, Trash2, Calendar, MapPin, Youtube } from 'lucide-react';
import CarSelectionTable from './CarSelectionTable';
import api from '../api/client';
import './ChampionshipTabs.css';

interface ChampionshipEventsTabProps {
  championship: Championship;
  tracks: Track[];
  cars: Car[];
  onChange: (updates: Partial<Championship>) => void;
}

const weatherOptions = [
  { value: 'GameModeSelectionWeatherType_CLEAR', label: 'Clear' },
  { value: 'GameModeSelectionWeatherType_SCATTERED_CLOUDS', label: 'Scattered Clouds' },
  { value: 'GameModeSelectionWeatherType_BROKEN_CLOUDS', label: 'Broken Clouds' },
  { value: 'GameModeSelectionWeatherType_OVERCAST', label: 'Overcast' },
  { value: 'GameModeSelectionWeatherType_DAMP', label: 'Damp' },
  { value: 'GameModeSelectionWeatherType_DRIZZLE', label: 'Drizzle' },
  { value: 'GameModeSelectionWeatherType_RAIN', label: 'Rain' },
  { value: 'GameModeSelectionWeatherType_HEAVY_RAIN', label: 'Heavy Rain' },
];

const weatherBehaviourOptions = [
  { value: 'GameModeSelectionWeatherBehaviour_DYNAMIC', label: 'Dynamic' },
  { value: 'GameModeSelectionWeatherBehaviour_STATIC', label: 'Static' },
];

// Convert minutes to seconds (backend expects seconds, UI shows minutes)
const minutesToSeconds = (minutes: number): number => minutes * 60;
const secondsToMinutes = (seconds: number): number => Math.round(seconds / 60);

const formatVodInputValue = (vodVideoId?: string): string => {
  if (!vodVideoId) return '';
  return /^[A-Za-z0-9_-]{11}$/.test(vodVideoId)
    ? `https://www.youtube.com/watch?v=${vodVideoId}`
    : vodVideoId;
};

const initialRound = (track = '', layout = '', eventNumber = 1, roundNumber = 1): ChampionshipRound => ({
  id: `${eventNumber}-${roundNumber}-${Date.now()}`,
  track,
  layout,
  date: new Date().toISOString().split('T')[0],
  startTime: '20:00',
  timezone: 'Europe/Amsterdam',
  practiceLength: minutesToSeconds(20), // 20 minutes = 1200 seconds
  qualifyLength: minutesToSeconds(10),  // 10 minutes = 600 seconds
  warmupLength: 0,
  raceLength: minutesToSeconds(20),     // 20 minutes = 1200 seconds
  weather: 'GameModeSelectionWeatherType_CLEAR',
  weatherBehaviour: 'GameModeSelectionWeatherBehaviour_STATIC',
  ingameTime: '14:00',
  raceId: `${eventNumber}-${roundNumber}-${Date.now()}`,
});

const initialEvent = (eventNumber = 1): ChampionshipEvent => ({
  id: `event-${eventNumber}-${Date.now()}`,
  eventNumber,
  rounds: [initialRound('', '', eventNumber, 1)],
  allowed_cars_list_full: [],
});

const ChampionshipEventsTab: React.FC<ChampionshipEventsTabProps> = ({
  championship,
  tracks,
  cars,
  onChange,
}) => {
  const events = championship.events || [];

  // signoutsByRoundId: raceId -> SimgridSignout[]
  const [signoutsByRaceId, setSignoutsByRaceId] = useState<Record<string, SimgridSignout[]>>({});
  const [signoutInput, setSignoutInput] = useState<Record<string, string>>({});  // raceId -> selected steamId

  const loadSignouts = useCallback(async () => {
    if (!championship.id) return;
    try {
      const res = await api.getChampionshipSignouts(championship.id);
      setSignoutsByRaceId(res.data.signouts || {});
    } catch {
      // non-fatal
    }
  }, [championship.id]);

  useEffect(() => {
    loadSignouts();
  }, [loadSignouts]);

  const handleSignOut = async (eventId: string, round: ChampionshipRound) => {
    const steamId = signoutInput[round.raceId];
    if (!steamId) return;
    const reg = (championship.registrations || []).find((r) => r.steamId === steamId);
    try {
      await api.signOutDriver(championship.id, eventId, round.id, steamId, reg?.driverName || steamId);
      await loadSignouts();
      setSignoutInput((prev) => ({ ...prev, [round.raceId]: '' }));
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to sign out driver');
    }
  };

  const handleSignIn = async (eventId: string, round: ChampionshipRound, steamId: string | number) => {
    try {
      await api.signInDriver(championship.id, eventId, round.id, String(steamId));
      await loadSignouts();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to sign driver back in');
    }
  };

  // Helper to check if a round starts before the previous round would end
  const checkRoundTimingConflict = (eventIndex: number, roundIndex: number): {
    warning: string;
    previousRoundEnd: string;
    currentRoundStart: string;
  } | null => {
    if (roundIndex === 0) {
      return null; // First round of event has no previous round in same event
    }

    const event = events[eventIndex];
    const currentRound = event.rounds[roundIndex];
    const previousRound = event.rounds[roundIndex - 1];

    if (!currentRound || !previousRound) {
      return null;
    }

    // Calculate when previous round ends (start + practice + qualify + race + buffer)
    // Note: durations are stored in seconds in the backend
    const previousStart = new Date(`${previousRound.date}T${previousRound.startTime}`);
    const totalDurationSeconds = (previousRound.practiceLength || 0) +
                                 (previousRound.qualifyLength || 0) +
                                 (previousRound.raceLength || 0) +
                                 (10 * 60); // 10 minute buffer for server transition
    const previousEnd = new Date(previousStart.getTime() + totalDurationSeconds * 1000);

    // Get current round start time
    const currentStart = new Date(`${currentRound.date}T${currentRound.startTime}`);

    // Check if current starts before previous ends
    if (currentStart < previousEnd) {
      return {
        warning: `This round starts before the previous round is expected to finish (including rotation time).`,
        previousRoundEnd: previousEnd.toLocaleString(),
        currentRoundStart: currentStart.toLocaleString(),
      };
    }

    return null;
  };

  const addEvent = () => {
    const newEvents = [...events, initialEvent(events.length + 1)];
    onChange({ events: newEvents });
  };

  const removeEvent = (index: number) => {
    const newEvents = events.filter((_, idx) => idx !== index).map((event, idx) => ({
      ...event,
      eventNumber: idx + 1,
    }));
    onChange({ events: newEvents });
  };

  const updateEvent = (index: number, updatedEvent: ChampionshipEvent) => {
    const newEvents = [...events];
    newEvents[index] = updatedEvent;
    onChange({ events: newEvents });
  };

  const updateRoundCount = (eventIndex: number, count: number) => {
    const event = events[eventIndex];
    const rounds = [...event.rounds];
    const track = event.rounds[0]?.track || '';
    const layout = event.rounds[0]?.layout || '';
    while (rounds.length < count) {
      rounds.push(initialRound(track, layout, event.eventNumber, rounds.length + 1));
    }
    updateEvent(eventIndex, { ...event, rounds: rounds.slice(0, count) });
  };

  const SESSION_FIELDS: Array<keyof ChampionshipRound> = [
    'practiceLength', 'qualifyLength', 'warmupLength', 'raceLength',
    'ingameTime', 'weather', 'weatherBehaviour',
  ];

  const isRoundSessionDefault = (round: ChampionshipRound): boolean => {
    const def = initialRound();
    return SESSION_FIELDS.every((f) => round[f] === def[f]);
  };

  const updateRound = (eventIndex: number, roundIndex: number, updatedRound: ChampionshipRound) => {
    const event = events[eventIndex];
    const newRounds = [...event.rounds];
    newRounds[roundIndex] = updatedRound;
    const newEvents = [...events];
    newEvents[eventIndex] = { ...event, rounds: newRounds };

    // When editing the first event's first round, propagate session fields to
    // later events whose rounds are still at default values (never been edited).
    if (eventIndex === 0 && roundIndex === 0 && events.length > 1) {
      for (let i = 1; i < newEvents.length; i++) {
        const laterEvent = newEvents[i];
        const laterRounds = laterEvent.rounds.map((r) => {
          if (!isRoundSessionDefault(r)) return r;
          const patch: Partial<ChampionshipRound> = {};
          for (const f of SESSION_FIELDS) {
            (patch as any)[f] = (updatedRound as any)[f];
          }
          return { ...r, ...patch };
        });
        newEvents[i] = { ...laterEvent, rounds: laterRounds };
      }
    }

    onChange({ events: newEvents });
  };

  const handleCarSelectionChange = (eventIndex: number, carName: string, selected: boolean) => {
    const event = events[eventIndex];
    const allowedCars = event.allowed_cars_list_full || [];

    let newAllowedCars: CarConfig[];
    if (selected) {
      if (allowedCars.some((c) => c.car_name === carName)) {
        newAllowedCars = allowedCars;
      } else {
        newAllowedCars = [...allowedCars, { car_name: carName, ballast: 0, restrictor: 0 }];
      }
    } else {
      newAllowedCars = allowedCars.filter((c) => c.car_name !== carName);
    }

    updateEvent(eventIndex, { ...event, allowed_cars_list_full: newAllowedCars });
  };

  const handleCarConfigChange = (eventIndex: number, carName: string, ballast: number, restrictor: number) => {
    const event = events[eventIndex];
    const newAllowedCars = (event.allowed_cars_list_full || []).map((c) =>
      c.car_name === carName ? { ...c, ballast, restrictor } : c
    );
    updateEvent(eventIndex, { ...event, allowed_cars_list_full: newAllowedCars });
  };

  return (
    <div className="championship-tab-content championship-events-tab">
      <div className="events-header">
        <h3>Championship Events</h3>
        <button className="add-event-button" onClick={addEvent}>
          <Plus size={16} />
          Add Event
        </button>
      </div>

      {events.length === 0 && (
        <div className="empty-state">
          <p>No events configured yet. Click "Add Event" to get started.</p>
        </div>
      )}

      {events.map((event, eventIndex) => (
        <div className="event-card" key={event.id}>
          <div className="event-card-header">
            <h4>
              <Calendar size={18} />
              Event {eventIndex + 1}
            </h4>
            <div className="event-actions">
              <div className="form-group rounds-selector">
                <label>Rounds:</label>
                <select
                  value={event.rounds.length}
                  onChange={(e) => updateRoundCount(eventIndex, Number(e.target.value))}
                >
                  {[1, 2, 3, 4].map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </div>
              {events.length > 1 && (
                <button className="remove-event-button" onClick={() => removeEvent(eventIndex)}>
                  <Trash2 size={14} />
                  Remove
                </button>
              )}
            </div>
          </div>

          {event.rounds.map((round, roundIndex) => (
            <div className="round-card" key={round.id}>
              <div className="round-header">
                <strong>
                  <MapPin size={14} />
                  Round {roundIndex + 1}
                </strong>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Track</label>
                  <select
                    value={round.track}
                    onChange={(e) => {
                      const track = e.target.value;
                      const layout = tracks.find((t) => t.track === track)?.layout || '';
                      updateRound(eventIndex, roundIndex, { ...round, track, layout });
                    }}
                  >
                    <option value="">Select track</option>
                    {Array.from(new Map(tracks.map((t) => [t.track, t])).values()).map((track) => (
                      <option key={track.track} value={track.track}>
                        {track.track}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Layout</label>
                  <select
                    value={round.layout}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, layout: e.target.value })}
                  >
                    <option value="">Select layout</option>
                    {tracks
                      .filter((t) => t.track === round.track)
                      .map((track) => (
                        <option key={`${track.track}-${track.layout}`} value={track.layout}>
                          {track.layout} ({track.max_pit_slot} spots)
                        </option>
                      ))}
                  </select>
                  {round.layout && (() => {
                    const t = tracks.find((t) => t.track === round.track && t.layout === round.layout);
                    return t ? (
                      <small style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                        {t.max_pit_slot} pit spots available
                      </small>
                    ) : null;
                  })()}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={round.date}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={round.startTime}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, startTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={round.timezone || 'UTC'}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, timezone: e.target.value })}
                  >
                    <option value="UTC">UTC</option>
                    <option value="Europe/Amsterdam">Europe/Amsterdam (CET/CEST)</option>
                    <option value="Europe/London">Europe/London (GMT/BST)</option>
                    <option value="America/New_York">America/New York (EST/EDT)</option>
                    <option value="America/Los_Angeles">America/Los Angeles (PST/PDT)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Weather</label>
                  <select
                    value={round.weather}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, weather: e.target.value })}
                  >
                    {weatherOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Weather Type</label>
                  <select
                    value={round.weatherBehaviour || 'GameModeSelectionWeatherBehaviour_STATIC'}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, weatherBehaviour: e.target.value })}
                  >
                    {weatherBehaviourOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>In-Game Time</label>
                  <input
                    type="time"
                    value={round.ingameTime}
                    onChange={(e) => updateRound(eventIndex, roundIndex, { ...round, ingameTime: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Practice Length (min)</label>
                  <input
                    type="number"
                    min="0"
                    value={secondsToMinutes(round.practiceLength)}
                    onChange={(e) =>
                      updateRound(eventIndex, roundIndex, { ...round, practiceLength: minutesToSeconds(Number(e.target.value)) })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Qualify Length (min)</label>
                  <input
                    type="number"
                    min="0"
                    value={secondsToMinutes(round.qualifyLength)}
                    onChange={(e) =>
                      updateRound(eventIndex, roundIndex, { ...round, qualifyLength: minutesToSeconds(Number(e.target.value)) })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Warm-up Length (min)</label>
                  <input
                    type="number"
                    min="0"
                    value={secondsToMinutes(round.warmupLength ?? 0)}
                    onChange={(e) =>
                      updateRound(eventIndex, roundIndex, { ...round, warmupLength: minutesToSeconds(Number(e.target.value)) })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Race Length (min)</label>
                  <input
                    type="number"
                    min="0"
                    value={secondsToMinutes(round.raceLength)}
                    onChange={(e) =>
                      updateRound(eventIndex, roundIndex, { ...round, raceLength: minutesToSeconds(Number(e.target.value)) })
                    }
                  />
                </div>
              </div>

              {(() => {
                const conflict = checkRoundTimingConflict(eventIndex, roundIndex);
                if (conflict) {
                  return (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 14px',
                      backgroundColor: 'var(--warning-bg, #fef3c7)',
                      border: '1px solid var(--warning-border, #fbbf24)',
                      borderRadius: '6px',
                      color: 'var(--warning-text, #92400e)',
                      fontSize: '0.9rem',
                      lineHeight: '1.5'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        ⚠️ Timing Conflict Warning
                      </div>
                      <div>
                        {conflict.warning} The previous round is expected to end around <strong>{conflict.previousRoundEnd}</strong>, but this round starts at <strong>{conflict.currentRoundStart}</strong>. The server may not be ready and will not automatically start.
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {championship.broadcasted && (
                <div className="form-row" style={{ marginTop: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Youtube size={14} />
                      VOD URL (YouTube)
                      <span style={{ fontWeight: 400, color: 'var(--text-muted, #888)', fontSize: '0.85em' }}>
                        — auto-captured, or paste override
                      </span>
                    </label>
                    <input
                      type="url"
                      placeholder={round.vodVideoId ? `https://www.youtube.com/watch?v=${round.vodVideoId}` : 'https://www.youtube.com/watch?v=...'}
                      value={formatVodInputValue(round.vodVideoId)}
                      onChange={(e) => {
                        const val = e.target.value.trim();
                        updateRound(eventIndex, roundIndex, { ...round, vodVideoId: val });
                      }}
                    />
                  </div>
                </div>
              )}

              {!championship.simgridManaged && (
                <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Sign-outs for this round</label>

                  {/* Current sign-outs */}
                  {(signoutsByRaceId[round.raceId] || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                      {(signoutsByRaceId[round.raceId] || []).map((s) => (
                        <span
                          key={s.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            fontSize: '0.8rem', padding: '3px 8px', borderRadius: '4px',
                            background: 'var(--surface-strong, rgba(255,255,255,0.07))',
                            border: '1px solid var(--border-color)',
                          }}
                        >
                          {s.signoutable}
                          <button
                            type="button"
                            onClick={() => handleSignIn(event.id, round, s.signoutable_id)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'var(--text-secondary)', fontSize: '0.75rem', padding: '0 2px',
                            }}
                            title="Sign back in"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Sign-out a driver */}
                  {(championship.registrations || []).filter((r) => r.status !== 'left').length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={signoutInput[round.raceId] || ''}
                        onChange={(e) => setSignoutInput((prev) => ({ ...prev, [round.raceId]: e.target.value }))}
                        style={{ flex: 1 }}
                      >
                        <option value="">Select driver to sign out…</option>
                        {(championship.registrations || [])
                          .filter((r) => {
                            if (r.status === 'left') return false;
                            const alreadyOut = (signoutsByRaceId[round.raceId] || []).some(
                              (s) => String(s.signoutable_id) === r.steamId
                            );
                            return !alreadyOut;
                          })
                          .map((r) => (
                            <option key={r.steamId} value={r.steamId}>
                              {r.driverName} #{r.carNumber}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="remove-event-button"
                        onClick={() => handleSignOut(event.id, round)}
                        disabled={!signoutInput[round.raceId]}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {!championship.sameCarsForAllRaces && (
            <div className="event-car-selection">
              <h5>Allowed Cars for This Event</h5>
              <CarSelectionTable
                cars={cars}
                selectedCars={(event.allowed_cars_list_full || []).map((c) => c.car_name)}
                carConfigs={
                  new Map(
                    (event.allowed_cars_list_full || []).map((c) => [
                      c.car_name,
                      { ballast: c.ballast, restrictor: c.restrictor },
                    ])
                  )
                }
                onSelectionChange={(carName, selected) => handleCarSelectionChange(eventIndex, carName, selected)}
                onConfigChange={(carName, ballast, restrictor) =>
                  handleCarConfigChange(eventIndex, carName, ballast, restrictor)
                }
              />
            </div>
          )}
        </div>
      ))}

      {championship.sameCarsForAllRaces && (
        <div className="shared-cars-notice">
          <p>
            <strong>Note:</strong> All events will use the same car configuration. Edit shared cars in the Details tab if needed (currently configured for all events).
          </p>
        </div>
      )}
    </div>
  );
};

export default ChampionshipEventsTab;
